import React, { useState, useEffect } from 'react';
import { useAuth } from 'shared-auth';
import { useNotification } from './NotificationSystem';
import * as pushService from 'shared-push';
import { BellIcon, XIcon } from 'shared-icons';

interface PushNotificationPromptProps {
  hasCompletedTest: boolean;
}

const PushNotificationPrompt: React.FC<PushNotificationPromptProps> = ({ hasCompletedTest }) => {
  // FIX: Removed 'token' as it is not provided by the AuthContext. Authentication is handled by httpOnly cookies.
  const { isAuthenticated } = useAuth();
  const [isVisible, setIsVisible] = useState(false);
  const [isChecking, setIsChecking] = useState(true);
  const notify = useNotification();

  useEffect(() => {
    const checkStatus = async () => {
      // Only proceed if a test has been completed and the user is logged in
      if (!hasCompletedTest || !isAuthenticated) {
        setIsChecking(false);
        return;
      }

      const isSupported = pushService.isPushSupported();
      if (!isSupported) {
        setIsChecking(false);
        return;
      }

      const hasBeenPrompted = localStorage.getItem('push_prompted');
      if (hasBeenPrompted) {
        setIsChecking(false);
        return;
      }
      
      const subscribed = await pushService.isSubscribed();
      if (!subscribed && Notification.permission === 'default') {
        // Only show prompt if they haven't made a decision yet
        setTimeout(() => {
            setIsVisible(true);
            setIsChecking(false);
        }, 3000); // Wait 3 seconds after test finishes
      } else {
        setIsChecking(false);
      }
    };

    checkStatus();
  }, [hasCompletedTest, isAuthenticated]);

  const handleDismiss = () => {
    setIsVisible(false);
    localStorage.setItem('push_prompted', 'true');
  };

  const handleEnable = async () => {
    try {
      const subscription = await pushService.subscribeUser();
      if (subscription) {
        // FIX: Removed 'token' argument. The API call is authenticated via httpOnly cookie.
        await pushService.sendSubscriptionToBackend(subscription);
        notify({ type: 'success', title: 'Notifications Enabled!', message: 'You will now receive performance alerts & updates.' });
      } else {
        // User denied permission, don't show prompt again
        localStorage.setItem('push_prompted', 'true');
      }
    } catch (error) {
       const message = error instanceof Error ? error.message : 'An unknown error occurred.';
       notify({ type: 'error', title: 'Could not enable notifications', message });
    }
    setIsVisible(false);
  };
  
  if (!isVisible || isChecking) {
    return null;
  }

  return (
    <div className="fixed bottom-4 right-4 z-50 w-full max-w-sm" style={{ animation: 'toast-in 0.5s' }}>
      <div className="bg-white dark:bg-slate-800 shadow-2xl rounded-lg p-4 ring-1 ring-black ring-opacity-5 dark:ring-white/10">
        <div className="flex items-start">
          <div className="flex-shrink-0 pt-0.5">
            <BellIcon className="w-6 h-6 text-cyan-500" />
          </div>
          <div className="ml-3 w-0 flex-1">
            <p className="text-sm font-bold text-slate-900 dark:text-white">Get Performance Alerts</p>
            <p className="mt-1 text-sm text-slate-600 dark:text-slate-300">
              Enable notifications to get an alert if your internet speed drops significantly.
            </p>
            <div className="mt-3 flex space-x-3">
              <button
                onClick={handleEnable}
                className="inline-flex items-center px-3 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-cyan-600 hover:bg-cyan-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-cyan-500"
              >
                Enable
              </button>
              <button
                onClick={handleDismiss}
                className="inline-flex items-center px-3 py-2 border border-gray-300 dark:border-slate-600 text-sm font-medium rounded-md text-gray-700 dark:text-slate-200 bg-white dark:bg-slate-700 hover:bg-gray-50 dark:hover:bg-slate-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-cyan-500"
              >
                No Thanks
              </button>
            </div>
          </div>
          <div className="ml-4 flex-shrink-0 flex">
            <button onClick={handleDismiss} className="inline-flex text-gray-400 dark:text-slate-400 hover:text-gray-500 dark:hover:text-slate-300" aria-label="Dismiss">
              <XIcon className="h-5 w-5" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PushNotificationPrompt;